/* eslint-disable no-useless-escape */
/* eslint-disable no-plusplus */
import type { EditorState } from '@tiptap/pm/state'
import { isWindowDefined } from '@chegg/utils'
import { createPaper } from '@chegg-wt/core/utils/paraphraser/paperIndexedDb'
import { setResetPaper } from '@chegg-wt/core/state/paraphraser/actions'
import history from '@chegg-wt/core/utils/paraphraser/history/history'
import { Dispatch } from 'redux'
import { EditorSelection, Feedback, FeedbackType } from '../../types/paraphraser'
import { SentenceOffset } from '../../state/paraphraser/types'
import { PaperCheck_paperCheck_sentences as PaperCheckSentence } from '../../oneGraphQueries/__generated__/PaperCheck'
import getFeatureEditor from './getEditor'
import { CreateGuestUserPaperProps } from './types'

export const getSelection = (state: EditorState): EditorSelection | null => {
  if (!state) return null
  const { selection } = state
  const { from, to } = selection
  const selectedText = state.doc.textBetween(from, to, ' ')

  return {
    selectedText: selectedText.trim(),
    from,
    to,
  }
}

export const getWordCount = (text: string): number => {
  const trimmedText = text.trim()
  return trimmedText === '' ? 0 : trimmedText.split(/\s+/g).length
}

export const trimTextByWords = (text: string, wordLimit: number): string => {
  const words = text.split(/\s+/g)
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ')
  }
  return text
}

export const mapSentencesToSentenceOffsets = (
  sentences: PaperCheckSentence[],
  text: string
): SentenceOffset[] => {
  const timestamp = new Date().getTime()
  return sentences.map((sentence) => {
    const { offset, length } = sentence.position
    const sentenceText = text.substring(offset, offset + length)
    const mappedSentence: SentenceOffset = {
      id: sentence.id,
      originalText: sentenceText,
      lastUpdated: timestamp,
      ...sentence.position,
    }
    return mappedSentence
  })
}

export const getGrammarPosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
): { offset: number; length: number } => {
  const sentenceOffset = sentenceOffsets.find(
    (sentence) => sentence.id === feedback.sentenceId
  )?.offset

  if (!sentenceOffset)
    throw new Error(
      `sentence offset not found for feedback.sentenceId: ${feedback.sentenceId}`
    )

  return {
    offset: sentenceOffset + feedback.position.offset,
    length: feedback.position.length,
  }
}

export const getParaphrasePosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
): { offset: number; length: number } => {
  const sentence = sentenceOffsets.find((phrase) => phrase.id === feedback.sentenceId)

  if (!sentence)
    throw new Error(
      `sentence offset not found for feedback.sentenceId: ${feedback.sentenceId}`
    )

  return { offset: sentence.offset, length: sentence.length }
}

export const getFeedbackPosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
) => {
  return feedback.feedbackType === FeedbackType.PARAPHRASE
    ? getParaphrasePosition(feedback, sentenceOffsets)
    : getGrammarPosition(feedback, sentenceOffsets)
}

export const getTippyInstance = () =>
  isWindowDefined
    ? // eslint-disable-next-line no-underscore-dangle
      (document.querySelector('.tippy-box')?.parentElement as any)?._tippy || null
    : null

export const storeProofReaderPapersInLocal = (): boolean => {
  // This function will be updated to return true when user is guest user
  return true
}

export const createGuestUserPaper = async ({
  paperId,
  title,
  paperContent,
  status,
}: CreateGuestUserPaperProps): Promise<string> => {
  if (paperId || !storeProofReaderPapersInLocal()) return null

  const newPaperId = await createPaper(
    title,
    paperContent,
    {
      feedbackList: [],
      sentences: [],
      totalFeedbackCount: 0,
    },
    false,
    paperId,
    status
  )
  return newPaperId
}

// Remove all underlines from editor view and storage
export const clearAllUnderlines = () => {
  const editor = getFeatureEditor()
  editor.commands.removeCustomUnderline({
    id: 'remove-editor-underline',
    offset: 1,
    length: editor.getText().length,
  })

  editor.storage.comments = {}
}

export const addUnderlineEditorText = (
  feedbacks: Feedback[],
  sentenceOffsets: SentenceOffset[],
  activeTabIndex?: number
) => {
  if (activeTabIndex === 0 || activeTabIndex === 1) {
    const editor = getFeatureEditor()
    clearAllUnderlines()

    feedbacks.forEach((feedback) => {
      const { id } = feedback

      let underlinePosition = null
      if (activeTabIndex === 0 && feedback.feedbackType === FeedbackType.GRAMMAR) {
        underlinePosition = getGrammarPosition(feedback, sentenceOffsets)
      }
      if (activeTabIndex === 1 && feedback.feedbackType === FeedbackType.PARAPHRASE) {
        underlinePosition = getParaphrasePosition(feedback, sentenceOffsets)
      }

      if (underlinePosition) {
        editor.commands.setCustomUnderline({
          id,
          offset: underlinePosition.offset,
          length: underlinePosition.length,
          type: feedback.feedbackType,
        })
      }
    })
  }
}

export const getCurrentDateInMMDDYY = (): string => {
  const date = new Date()

  const dateFormatted = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)

  return dateFormatted
}

export const scrollMarkContentInEditor = (feedbackId: string) => {
  if (isWindowDefined && feedbackId) {
    const ADJUSTED_OFFSET_TOP = 75
    const currHightlightMark = window.document.querySelector(
      `[data-test="customunderline-mark-${feedbackId}"]`
    ) as HTMLDivElement | null
    const parentElement = window.document.getElementById('editor-container')

    if (!currHightlightMark || !parentElement) {
      return
    }

    const { top: parentTop, bottom: parentBottom } = parentElement.getBoundingClientRect()
    const {
      top: childTop,
      bottom: childBottom,
    } = currHightlightMark.getBoundingClientRect()

    if (childTop > parentTop && childBottom < parentBottom) {
      // don't scroll
      return
    }

    parentElement.scrollTo({
      top: currHightlightMark.offsetTop - ADJUSTED_OFFSET_TOP,
      behavior: 'smooth',
    })
  }
}

export const clearWindowSelection = () => {
  const windowSelection = window.getSelection()
  if (windowSelection.rangeCount > 0) {
    windowSelection.removeAllRanges()
  }
}

export const transformSentencesToSaveInDb = (sentences) => {
  const transformedSentences = sentences.map((sentence) => {
    return {
      id: sentence.id || null,
      position: {
        length: sentence.length ?? null,
        offset: sentence.offset ?? null,
      },
    }
  })
  return transformedSentences
}

export const transformSentencesForUi = (sentences) => {
  const transformedSentences = sentences.map((sentence) => ({
    id: sentence.id || null,
    length: sentence.position.length || null,
    offset: sentence.position.offset || null,
  }))

  return transformedSentences
}

export const resetPaperAndEditor = (dispatch: Dispatch) => {
  const editor = getFeatureEditor()
  editor.commands.clearContent()
  dispatch(setResetPaper())
  history.clear()
}

export const filterFeedbackInput = (feedback) => {
  const { id, sentenceId, feedbackType, reason, changeType, suggestedText } = feedback

  // Sanitize position
  const sanitizedPosition = feedback.position
    ? {
        offset: feedback.position.offset,
        length: feedback.position.length,
      }
    : null

  return {
    id,
    sentenceId,
    feedbackType: feedbackType as any,
    reason: reason || null,
    changeType: changeType || null,
    position: sanitizedPosition,
    suggestedText,
  }
}

export const sanitizeFeedbackList = (feedbackList) => {
  return feedbackList.map(filterFeedbackInput)
}
